import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { pedirPuente, versionUso } from '#uso';

const MAX_TEXTO = 1000;

type Tipo = 'fallo' | 'idea';
type Estado = 'escribiendo' | 'enviando' | 'enviado' | 'error' | 'demasiados';

/**
 * «Algo no va / Pide una función» (Puente de mando): la persona escribe y le
 * llega a quien lleva Rumbo con su nombre, la pantalla y la versión. El
 * servidor tapa las cifras largas (por si alguien pega un IBAN).
 */
export function AlgoNoVaSettings() {
  const { t } = useTranslation();
  const [tipo, setTipo] = useState<Tipo>('fallo');
  const [textoLibre, setTextoLibre] = useState('');
  const [estado, setEstado] = useState<Estado>('escribiendo');

  const enviar = async () => {
    setEstado('enviando');
    try {
      await pedirPuente('/peticion', {
        metodo: 'POST',
        cuerpo: {
          tipo,
          texto: textoLibre.trim(),
          pantalla: '/settings',
          version: versionUso(),
        },
      });
      setTextoLibre('');
      setEstado('enviado');
    } catch (e) {
      setEstado(
        e instanceof Error && e.message === 'too-many' ? 'demasiados' : 'error',
      );
    }
  };

  if (estado === 'enviado') {
    return (
      <View style={{ gap: 12 }}>
        <Text style={{ ...texto.cuerpo, color: color.fg }}>
          <Trans>
            Received. I read everything, and if I need more details I will write
            to you by email.
          </Trans>
        </Text>
        <Boton variante="fantasma" onPress={() => setEstado('escribiendo')}>
          <Trans>Write something else</Trans>
        </Boton>
      </View>
    );
  }

  return (
    <View style={{ gap: 12 }}>
      <Segmentado<Tipo>
        etiqueta={t('What do you want to tell me?')}
        opciones={[
          { valor: 'fallo', texto: t('Something is wrong') },
          { valor: 'idea', texto: t('Ask for a feature') },
        ]}
        valor={tipo}
        onChange={setTipo}
        testId="algo-no-va-tipo"
        estirar
        alto={TACTIL}
      />
      <textarea
        aria-label={
          tipo === 'fallo'
            ? t('What happened?')
            : t('What would you like Rumbo to do?')
        }
        data-testid="algo-no-va-texto"
        value={textoLibre}
        maxLength={MAX_TEXTO}
        rows={4}
        placeholder={
          tipo === 'fallo'
            ? t('What happened and on which screen?')
            : t('What would you like Rumbo to do?')
        }
        onChange={e => {
          setTextoLibre(e.target.value);
          if (estado !== 'escribiendo' && estado !== 'enviando') {
            setEstado('escribiendo');
          }
        }}
        style={{
          width: '100%',
          minHeight: 110,
          borderRadius: radio.boton,
          backgroundColor: color.surface2,
          border: `1px solid ${color.line}`,
          padding: '12px 14px',
          fontSize: 16,
          lineHeight: 1.4,
          color: color.fg,
          boxSizing: 'border-box',
          fontFamily: 'inherit',
          resize: 'vertical',
        }}
      />
      <Text style={{ ...texto.secundario, color: color.fg3 }}>
        <Trans>
          It reaches me with your name and the app version. Don&apos;t write
          amounts or account numbers.
        </Trans>
      </Text>
      {estado === 'error' && (
        <Text style={{ ...texto.secundario, color: color.bad }}>
          <Trans>It could not be sent. Try again in a while.</Trans>
        </Text>
      )}
      {estado === 'demasiados' && (
        <Text style={{ ...texto.secundario, color: color.bad }}>
          <Trans>That is a lot for today. Try again tomorrow.</Trans>
        </Text>
      )}
      <Boton
        bloque
        data-testid="algo-no-va-enviar"
        isDisabled={textoLibre.trim() === '' || estado === 'enviando'}
        onPress={() => void enviar()}
      >
        <Trans>Send</Trans>
      </Boton>
    </View>
  );
}
